import { useEffect, useState } from "react";
import {
  PlatformService,
  ServiceRequestStatusesEnum,
  FeedbackTypesEnum,
  FeedbackVM,
} from "@propertelligent/client-api";
import apiHelper from "../../../utils/apiHelper";
import SimpleSpinner from "../../loader";
import EmptyState from "../../../newComponents/emptystate";
import { GetImages } from "../../../utils/GetImages";
import { NotificationType, PubSub, TopicsEnum } from "../../../utils/pubsub";
import "../platform-portal.css";

const FEEDBACK_TYPE_LABELS: Record<number, string> = {
  [FeedbackTypesEnum.FeatureRequest]: "Feature Request",
  [FeedbackTypesEnum.ProductSupport]: "Product Support",
  [FeedbackTypesEnum.ReportBug]: "Report Bug",
  [FeedbackTypesEnum.DataUpdateRequest]: "Data Update Request",
  [FeedbackTypesEnum.BillingQuestion]: "Billing Question",
  [FeedbackTypesEnum.Other]: "Other",
};

const STATUS_OPTIONS = [
  { value: ServiceRequestStatusesEnum.New, label: "New" },
  { value: ServiceRequestStatusesEnum.Estimating, label: "Estimating" },
  { value: ServiceRequestStatusesEnum.InProgress, label: "In Progress" },
  { value: ServiceRequestStatusesEnum.VerificationPending, label: "Verification Pending" },
  { value: ServiceRequestStatusesEnum.PaymentPending, label: "Payment Pending" },
  { value: ServiceRequestStatusesEnum.Completed, label: "Completed" },
];

const SupportRequests = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<FeedbackVM[]>([]);
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const res = await apiHelper(
      PlatformService.getApiFeedbackPlatformAll,
      { showNotification: false }
    );
    const success = res?.data?.success;
    setRows(Array.isArray(success) ? success : []);
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleStatusChange = async (id: number, status: ServiceRequestStatusesEnum) => {
    setUpdatingId(id);
    const res = await apiHelper(
      PlatformService.putApiFeedbackStatus,
      {
        showNotification: true,
        successMessage: "Support request status updated",
        failureMessage: "Unable to update status",
      },
      id,
      status
    );
    setUpdatingId(null);
    if (res?.data?.success) {
      setRows((prev) =>
        prev.map((r) => (r.id === id ? { ...r, feedbackStatus: status } : r))
      );
    } else {
      // Re-sync on failure
      PubSub.publish(TopicsEnum.Notification.toString(), {
        type: NotificationType.Failure,
        title: "Failure",
        message: "Unable to update status",
      });
      fetchData();
    }
  };

  return (
    <div className="platform-portal-page">
      <div className="top-listing-header-top">
        <h2 className="H2">Support Requests</h2>
      </div>

      {loading ? (
        <div className="platform-loadmore">
          <SimpleSpinner />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={GetImages.emptyStateData}
          heading="No support requests"
          isButton={false}
          isBorder={false}
        />
      ) : (
        <div className="platform-table-wrapper">
          <table className="platform-table">
            <thead>
              <tr>
                <th>Type</th>
                <th>Description</th>
                <th>Created</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{FEEDBACK_TYPE_LABELS[row.feedbackTypeId as number] || "—"}</td>
                  <td style={{ whiteSpace: "normal", maxWidth: 420 }}>
                    {row.feedbackDescription || "—"}
                  </td>
                  <td>{(row as any)?.formatted?.Created || row.created || "—"}</td>
                  <td>
                    <select
                      className="platform-status-select"
                      value={row.feedbackStatus ?? ServiceRequestStatusesEnum.New}
                      disabled={updatingId === row.id}
                      onChange={(e) =>
                        handleStatusChange(
                          row.id as number,
                          Number(e.target.value) as ServiceRequestStatusesEnum
                        )
                      }
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SupportRequests;
